import React, { useState } from 'react';
import { ChevronRight, Save, Calendar, Plus, Trash2, Info } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

// Tooltip wrapper component for consistent styling - More visible version
const FieldTooltip = ({ content }) => (
  <TooltipProvider>
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <button type="button" className="ml-2 text-amber-400 hover:text-amber-300 transition-colors inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-400/20 hover:bg-amber-400/30 border border-amber-400/50">
          <Info size={12} strokeWidth={2.5} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs bg-slate-900 border-amber-500/50 text-slate-100 text-sm p-3 shadow-lg shadow-amber-500/10">
        <p>{content}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const Step1Personal = ({ data, onNext, onSaveAndExit, saving }) => {
  const [formData, setFormData] = useState({
    testator_name: data.testator_name || '',
    father_name: data.father_name || '',
    mother_name: data.mother_name || '',
    gender: data.gender || '',
    dob: data.dob || '',
    religion: data.religion || '',
    marital_status: data.marital_status || '',
    spouse_details: data.spouse_details || {},
    address: data.address || { line1: '', line2: '', city: '', state: '', pin: '' },
    children: data.children || [],
    pets: data.pets || []
  });

  const addChild = () => {
    setFormData(prev => ({
      ...prev,
      children: [...prev.children, { name: '', is_minor: false, guardian: '' }]
    }));
  };

  const removeChild = (index) => {
    setFormData(prev => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== index)
    }));
  };

  const updateChild = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      children: prev.children.map((child, i) => 
        i === index ? { ...child, [field]: value } : child
      )
    }));
  };

  const addPet = () => {
    setFormData(prev => ({
      ...prev,
      pets: [...prev.pets, { name: '', type: '', guardian: '', care_instructions: '', fund: '' }]
    }));
  };

  const removePet = (index) => {
    setFormData(prev => ({
      ...prev,
      pets: prev.pets.filter((_, i) => i !== index)
    }));
  };

  const updatePet = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      pets: prev.pets.map((pet, i) => 
        i === index ? { ...pet, [field]: value } : pet
      )
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'address.pin' && value && !/^\d*$/.test(value)) {
      return;
    }

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: processedValue }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: processedValue }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext(formData);
  };

  const inputClass = "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20";
  const selectClass = "bg-slate-800 border-slate-700 text-white w-full p-2 rounded-md focus:border-amber-500 focus:ring-1 focus:ring-amber-500/20";
  const labelClass = "text-slate-300 font-medium";

  return (
    <form onSubmit={handleSubmit} className="space-y-8" data-testid="step1-form">
      {/* Personal Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">Personal Information</h3>
        
        <div className="space-y-2">
          <Label className={labelClass}>
            Full Name *
            <FieldTooltip content="As per Aadhaar" />
          </Label>
          <Input
            type="text"
            name="testator_name"
            value={formData.testator_name}
            onChange={handleChange}
            maxLength="50"
            required
            className={inputClass}
            data-testid="testator-name"
          />
          <span className="text-xs text-slate-500">{formData.testator_name.length}/50 characters</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className={labelClass}>Gender *</Label>
            <select name="gender" value={formData.gender} onChange={handleChange} required className={selectClass} data-testid="gender">
              <option value="">Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label className={labelClass}>Date of Birth *</Label>
            <Input
              type="date"
              name="dob"
              value={formData.dob || ''}
              onChange={handleChange}
              max={new Date().toISOString().split('T')[0]}
              required
              className={inputClass}
              data-testid="dob"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className={labelClass}>
              Father's Name *
              <FieldTooltip content="As per Aadhaar" />
            </Label>
            <Input type="text" name="father_name" value={formData.father_name} onChange={handleChange} maxLength="50" required className={inputClass} />
          </div>
          <div className="space-y-2">
            <Label className={labelClass}>
              Mother's Name *
              <FieldTooltip content="As per Aadhaar" />
            </Label>
            <Input type="text" name="mother_name" value={formData.mother_name} onChange={handleChange} maxLength="50" required className={inputClass} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className={labelClass}>
              Religion *
              <FieldTooltip content="The religion of the testator determines the succession to his estate. For example, succession among Hindus is governed by Hindu Succession Laws and Muslims by Muslim Succession Laws." />
            </Label>
            <select name="religion" value={formData.religion} onChange={handleChange} required className={selectClass}>
              <option value="">Select</option>
              <option value="Hindu">Hindu</option>
              <option value="Muslim">Muslim</option>
              <option value="Christian">Christian</option>
              <option value="Sikh">Sikh</option>
              <option value="Buddhist">Buddhist</option>
              <option value="Jain">Jain</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label className={labelClass}>Marital Status *</Label>
            <select name="marital_status" value={formData.marital_status} onChange={handleChange} required className={selectClass}>
              <option value="">Select</option>
              <option value="Single">Single</option>
              <option value="Married">Married</option>
              <option value="Divorced">Divorced</option>
              <option value="Widowed">Widowed</option>
            </select>
          </div>
        </div>

        {formData.marital_status === 'Married' && (
          <div className="space-y-2">
            <Label className={labelClass}>
              Spouse Name *
              <FieldTooltip content="As per Aadhaar" />
            </Label>
            <Input type="text" name="spouse_details.name" value={formData.spouse_details.name || ''} onChange={handleChange} required className={inputClass} />
          </div>
        )}
      </div>

      {/* Address */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-b border-slate-700 pb-2 flex items-center">
          Address
        </h3>
        
        <div className="space-y-2">
          <Label className={labelClass}>Address Line 1 *</Label>
          <Input type="text" name="address.line1" value={formData.address.line1} onChange={handleChange} required className={inputClass} />
        </div>

        <div className="space-y-2">
          <Label className={labelClass}>Address Line 2</Label>
          <Input type="text" name="address.line2" value={formData.address.line2} onChange={handleChange} className={inputClass} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className={labelClass}>City *</Label>
            <Input type="text" name="address.city" value={formData.address.city} onChange={handleChange} required className={inputClass} />
          </div>
          <div className="space-y-2">
            <Label className={labelClass}>State *</Label>
            <Input type="text" name="address.state" value={formData.address.state} onChange={handleChange} required className={inputClass} />
          </div>
          <div className="space-y-2">
            <Label className={labelClass}>PIN Code *</Label>
            <Input type="text" name="address.pin" value={formData.address.pin} onChange={handleChange} maxLength="6" required className={inputClass} />
          </div>
        </div>
      </div>

      {/* Children */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-b border-slate-700 pb-2 flex items-center">
          Children
        </h3>
        <p className="text-sm text-slate-400">Add details of your children (if any)</p>
        
        {formData.children.map((child, index) => (
          <div key={index} className="bg-slate-800/50 p-4 rounded-lg space-y-4 border border-slate-700">
            <div className="space-y-2">
              <Label className={labelClass}>
                Child's Name *
                <FieldTooltip content="As per Aadhaar" />
              </Label>
              <Input type="text" value={child.name} onChange={(e) => updateChild(index, 'name', e.target.value)} required className={inputClass} />
            </div>
            
            <div className="flex items-center gap-4">
              <Label className={labelClass}>Is this child a minor?</Label>
              <button
                type="button"
                onClick={() => updateChild(index, 'is_minor', !child.is_minor)}
                className={`relative w-12 h-6 rounded-full transition-colors ${child.is_minor ? 'bg-amber-500' : 'bg-slate-600'}`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${child.is_minor ? 'left-7' : 'left-1'}`}></div>
              </button>
              <span className="text-sm text-slate-400">{child.is_minor ? 'Yes' : 'No'}</span>
            </div>
            
            {child.is_minor && (
              <div className="space-y-2">
                <Label className={labelClass}>Guardian Name *</Label>
                <Input type="text" value={child.guardian} onChange={(e) => updateChild(index, 'guardian', e.target.value)} placeholder="Enter guardian's full name" required className={inputClass} />
              </div>
            )}
            
            <button type="button" onClick={() => removeChild(index)} className="flex items-center gap-2 text-red-400 hover:text-red-300 text-sm">
              <Trash2 size={16} /> Remove Child
            </button>
          </div>
        ))}
        
        <Button type="button" onClick={addChild} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
          <Plus size={16} className="mr-2" /> Add Child
        </Button>
      </div>

      {/* Pets */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">Pets (Optional)</h3>
        <p className="text-sm text-slate-400">Provide for your pets after your passing</p>
        
        {formData.pets.map((pet, index) => (
          <div key={index} className="bg-slate-800/50 p-4 rounded-lg space-y-4 border border-slate-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelClass}>Pet Name *</Label>
                <Input type="text" value={pet.name} onChange={(e) => updatePet(index, 'name', e.target.value)} required className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label className={labelClass}>Type *</Label>
                <Input type="text" value={pet.type} onChange={(e) => updatePet(index, 'type', e.target.value)} placeholder="Dog, Cat, etc." required className={inputClass} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Guardian *</Label>
              <Input type="text" value={pet.guardian} onChange={(e) => updatePet(index, 'guardian', e.target.value)} placeholder="Who will care for the pet" required className={inputClass} />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Care Instructions</Label>
              <textarea value={pet.care_instructions} onChange={(e) => updatePet(index, 'care_instructions', e.target.value)} rows="2" placeholder="Special care needs, diet, medical conditions..." className={`${inputClass} w-full rounded-md p-2`}></textarea>
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Fund Allocation (₹)</Label>
              <Input type="number" value={pet.fund} onChange={(e) => updatePet(index, 'fund', e.target.value)} placeholder="Amount for pet care" className={inputClass} />
            </div>
            <button type="button" onClick={() => removePet(index)} className="flex items-center gap-2 text-red-400 hover:text-red-300 text-sm">
              <Trash2 size={16} /> Remove Pet
            </button>
          </div>
        ))}
        
        <Button type="button" onClick={addPet} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
          <Plus size={16} className="mr-2" /> Add Pet
        </Button>
      </div>

      {/* Navigation */}
      <div className="flex flex-wrap justify-between gap-4 pt-6 border-t border-slate-700">
        <Button type="button" onClick={() => onSaveAndExit(formData)} disabled={saving} variant="outline" className="border-emerald-600 text-emerald-400 hover:bg-emerald-500/10">
          <Save size={16} className="mr-2" /> Save & Exit
        </Button>
        <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
          Next: Executors & Witnesses <ChevronRight size={16} className="ml-2" />
        </Button>
      </div>
    </form>
  );
};

export default Step1Personal;
